import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { apiUrl } from "../utils/config";

// Convert a base64 URL-safe string to a Uint8Array (required by PushManager)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const SW_PATH = "/sw.js";

export function usePushSubscription(customerId = null) {
  const [supported, setSupported]     = useState(false);
  const [permission, setPermission]   = useState("default");
  const [subscribed, setSubscribed]   = useState(false);
  const [loading, setLoading]         = useState(false);

  // Check browser support and current state on mount
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    setSupported(true);
    setPermission(Notification.permission);

    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setSubscribed(!!sub);
      });
    });
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return;
    setLoading(true);
    try {
      // 1. Register service worker
      const reg = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;

      // 2. Ask for permission
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") {
        setLoading(false);
        return;
      }

      // 3. Fetch VAPID public key
      const { data } = await axios.get(`${apiUrl}/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(data.key);

      // 4. Subscribe via PushManager
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // 5. Send subscription to backend
      await axios.post(`${apiUrl}/push/subscribe`, {
        ...subscription.toJSON(),
        customer_id: customerId,
      });

      setSubscribed(true);
    } catch (err) {
      console.error("[push] subscribe error:", err);
    } finally {
      setLoading(false);
    }
  }, [supported, customerId]);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.post(`${apiUrl}/push/unsubscribe`, { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      console.error("[push] unsubscribe error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { supported, permission, subscribed, loading, subscribe, unsubscribe };
}
